import { useQueryClient } from '@tanstack/react-query';

import { useToast } from '@aslanpay/portal-common-lib';

import RecognitionValuesManager from 'app/components/Settings/RewardAndRecognitionTab/CompanyValues/RecognitionValuesManager';
import {
  ManagedRecognitionValue,
  RecognitionValueFormState,
} from 'app/components/Settings/RewardAndRecognitionTab/CompanyValues/types';

import useFetchRecognitionDefaultValues from 'app/hooks/useFetchRecognitionDefaultValues';
import useFetchRecognitionCards from 'app/hooks/useFetchRecognitionCards';
import useRecognitionDefaultValueMutations from 'app/hooks/useRecognitionDefaultValueMutations';
import useRecognitionCardMutations from 'app/hooks/useRecognitionCardMutations';

import { uploadRecognitionCardImage } from 'app/utils/recognitionCardUpload';

function DefaultValuesSection() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data: defaultsData, isInitialLoading: isLoadingValues } =
    useFetchRecognitionDefaultValues();
  // No companyId => the system-default card pool shared across all companies.
  const { data: cardsData, isInitialLoading: isLoadingCards } = useFetchRecognitionCards();

  const { createDefaultValue, updateDefaultValue, deleteDefaultValue } =
    useRecognitionDefaultValueMutations();
  const { createCard } = useRecognitionCardMutations();

  const values: ManagedRecognitionValue[] = defaultsData?.list ?? [];
  const availableCards = cardsData?.list ?? [];

  const invalidateValues = () =>
    queryClient.invalidateQueries({ queryKey: ['fetchRecognitionDefaultValues'] });
  const invalidateCards = () =>
    queryClient.invalidateQueries({ queryKey: ['fetchRecognitionCards', undefined] });

  const notifySaved = () =>
    toast({
      position: 'top-right',
      status: 'success',
      title: 'Saved',
      description: 'The default recognition value was saved.',
    });

  const handleCreate = async (form: RecognitionValueFormState) => {
    await createDefaultValue.mutateAsync({
      name: form.name,
      description: form.description || null,
      emoji: form.emoji,
      allowGifs: form.allowGifs,
      cardIds: form.cardIds,
    });
    await invalidateValues();
    notifySaved();
  };

  const handleUpdate = async (defaultValueId: string, form: RecognitionValueFormState) => {
    await updateDefaultValue.mutateAsync({
      defaultValueId,
      body: {
        name: form.name,
        description: form.description || null,
        emoji: form.emoji,
        allowGifs: form.allowGifs,
        cardIds: form.cardIds,
      },
    });
    await invalidateValues();
    notifySaved();
  };

  const handleDelete = async (defaultValueId: string) => {
    await deleteDefaultValue.mutateAsync(defaultValueId);
    await invalidateValues();
    toast({
      position: 'top-right',
      status: 'success',
      title: 'Deleted',
      description: 'The default recognition value was deleted.',
    });
  };

  const handleUploadCard = async (file: File) => {
    const contentUrl = await uploadRecognitionCardImage(file);
    const card = await createCard.mutateAsync({ contentUrl });
    await invalidateCards();
    return card.id;
  };

  return (
    <RecognitionValuesManager
      description="These defaults seed the values and cards for every newly created company."
      values={values}
      availableCards={availableCards}
      isLoading={isLoadingValues || isLoadingCards}
      showEnabledToggle={false}
      isSaving={createDefaultValue.isLoading || updateDefaultValue.isLoading}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      onUploadCard={handleUploadCard}
    />
  );
}

export default DefaultValuesSection;
